import React from 'react';
import { motion } from 'framer-motion';
import { ActivityIcon, TrendingUpIcon } from 'lucide-react';
import { tickers } from '../data/content';
import { CandleChart } from './charts/CandleChart';
import { Sparkline } from './charts/Sparkline';
export function HeroDashboard() {
  return <motion.div initial={{
    opacity: 0,
    y: 24,
    scale: 0.98
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} transition={{
    duration: 0.65,
    delay: 0.15,
    ease: [0.23, 1, 0.32, 1]
  }} className="relative" aria-label="Vista ilustrativa de un panel de mercados" data-id="element-120" data-mp="2clf897ylngm5">
      <div className="pointer-events-none absolute -inset-10 -z-10 rounded-[48px] opacity-70" style={{
      background: 'radial-gradient(60% 50% at 70% 25%, rgba(247,162,59,0.22), rgba(5,5,5,0) 70%)'
    }} data-id="element-121" data-mp="30ahpwft8h9gn" />

      {/* Panel principal: gráfico */}
      <div className="glass overflow-hidden rounded-3xl p-5 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.9)]" data-id="element-122" data-mp="1jw7x99hykyk0">
        <div className="flex items-start justify-between gap-4" data-id="element-123" data-mp="31s8iuizwjlfn">
          <div data-id="element-124" data-mp="31sqlbxd5qvw0">
            <div className="flex items-center gap-2" data-id="element-125" data-mp="31q4igb17p4k4">
              <span className="text-[15px] font-semibold text-white" data-id="element-126" data-mp="35s98z0wd5l2j">BTC/USDT</span>
              <span className="rounded-full bg-brand-dim px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-brand" data-id="element-127" data-mp="2rukmjomne5vd">
                Perp
              </span>
            </div>
            <p className="mt-1 text-[11px] text-ink-muted" data-id="element-128" data-mp="2i3pzii1kv28v">Vista ilustrativa · datos ficticios</p>
          </div>
          <div className="flex items-center gap-1.5 rounded-full border border-hairline px-2.5 py-1" data-id="element-129" data-mp="2mvq4iowi8lcb">
            <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-gain" data-id="element-130" data-mp="294l2jg5nzgyt" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted" data-id="element-131" data-mp="2e1v1bmey14zb">
              Demo
            </span>
          </div>
        </div>

        <div className="mt-4 flex items-end gap-1.5" data-id="element-132" data-mp="1z7gf7d031fgf">
          {['15m', '1H', '4H', '1D'].map((tf, i) => <span key={tf} className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors duration-200 ease-smooth ${i === 1 ? 'bg-white/10 text-white' : 'text-ink-muted'}`} data-id="element-133" data-mp="1kzlixrur5fyn" data-mpi={i}>
              {tf}
            </span>)}
        </div>

        <div className="mt-2" data-id="element-134" data-mp="16bgwqdwhqmb4">
          <CandleChart height={188} seed={3} data-id="element-135" data-mp="16c8vfxldx05m" />
        </div>
      </div>

      {/* Tarjeta: tendencias */}
      <motion.div initial={{
      opacity: 0,
      x: -18
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.6,
      delay: 0.35,
      ease: [0.23, 1, 0.32, 1]
    }} className="glass mt-4 rounded-3xl p-5 lg:absolute lg:-left-16 lg:top-[62%] lg:mt-0 lg:w-[268px] lg:shadow-[0_30px_80px_-30px_rgba(0,0,0,0.95)]" data-id="element-136" data-mp="23xodsn9ze74d">
        <div className="flex items-center gap-2" data-id="element-137" data-mp="il3bb68evlpi">
          <TrendingUpIcon className="h-3.5 w-3.5 text-brand" aria-hidden="true" data-id="element-138" data-mp="imo9d5txpms4" />
          <h3 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-muted" data-id="element-139" data-mp="dkyutg7i6qza">
            Tendencias
          </h3>
        </div>
        <ul className="mt-3.5 space-y-2.5" data-id="element-140" data-mp="2p7iv5a6ekvcj">
          {tickers.map(t => <li key={t.pair} className="flex items-center justify-between gap-3" data-id="element-141" data-mp="2u8q4ves3w4lu">
              <span className="text-[13px] font-medium text-white" data-id="element-142" data-mp="2z6roq1xcw7yd">{t.pair}</span>
              <span className={`text-[13px] font-semibold tabular-nums ${t.change >= 0 ? 'text-gain' : 'text-loss'}`} data-id="element-143" data-mp="33yjismfq0vt9">
                {t.change >= 0 ? '+' : ''}
                {t.change.toFixed(2)}%
              </span>
            </li>)}
        </ul>
      </motion.div>

      {/* Tarjeta: mercados populares */}
      <motion.div initial={{
      opacity: 0,
      x: 18
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.6,
      delay: 0.45,
      ease: [0.23, 1, 0.32, 1]
    }} className="glass mt-4 rounded-3xl p-5 lg:absolute lg:-right-10 lg:-bottom-24 lg:mt-0 lg:w-[280px] lg:shadow-[0_30px_80px_-30px_rgba(0,0,0,0.95)]" data-id="element-144" data-mp="3mxdutnnsvi5q">
        <div className="flex items-center gap-2" data-id="element-145" data-mp="3iync7q1lw1ge">
          <ActivityIcon className="h-3.5 w-3.5 text-brand" aria-hidden="true" data-id="element-146" data-mp="3e1n3f7ddgkll" />
          <h3 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-muted" data-id="element-147" data-mp="3s0wig3pud8nz">
            Mercados populares
          </h3>
        </div>
        <ul className="mt-3.5 space-y-3" data-id="element-148" data-mp="3o0mbanqbcc7j">
          {tickers.map(t => <li key={t.pair} className="flex items-center justify-between gap-3" data-id="element-149" data-mp="308xil69kxlq2">
              <span className="text-[13px] font-medium text-white" data-id="element-150" data-mp="2vb5pi8x77tka">{t.pair}</span>
              <Sparkline points={t.trend} positive={t.change >= 0} data-id="element-151" data-mp="3920omthwawao" />
            </li>)}
        </ul>
      </motion.div>
    </motion.div>;
}